import React from 'react';

import {
  useCateringLocations,
  useCart,
  selectCateringLocationBySlug,
  useUpdateCateringLocation,
  selectPickupLocations,
  selectDeliverToAddressLocations,
  selectClosedLocations,
  useUpdateCart
} from 'catering/hooks/query';

import PageLoadSpinner from 'components/PageLoadSpinner';
import { ScreenContainer } from 'components/Containers';
import SeoTitleUpdater from 'components/SeoTitleUpdater';

import { useAddressStateForLocationScreen } from 'catering/hooks';
import CateringLocationCard from 'catering/components/Location/CateringLocationCard';
import CateringLocationScreenHero from 'catering/components/Location/CateringLocationScreenHero';
import AllLocationsList from 'components/Location/AllLocationsList';
import { useOpenLocationChangeNotificationModal } from 'catering/components/Modals/NotificationModals';

import {
  useRedirectToLocationOffline,
  useRedirectToOrderSettingsForSingleLocation,
  useGoToOrderSettings
} from 'catering/hooks/navigation';

import { translateAddressFromComponents } from 'helpers/addressHelpers';
import { fulfillmentOptions } from 'constants/fulfillmentOptions';
import { useQueryParams } from 'catering/hooks/useQueryParams';
import { validateReferralSource } from 'bento-ordering/base/utils/validateReferralSource';
import { validateUTMCampaign } from 'bento-ordering/base/utils/validateUTMCampaign';

function LocationsScreen() {
  const queryParams = useQueryParams();
  const updateCart = useUpdateCart();
  const [address, setAddress] = useAddressStateForLocationScreen();
  useRedirectToLocationOffline();
  useRedirectToOrderSettingsForSingleLocation();

  const goToOrderSettings = useGoToOrderSettings();
  const openLocationChangeNotificationModal = useOpenLocationChangeNotificationModal();

  const {
    isLoading: isLocationsLoading,
    data: cateringLocations
  } = useCateringLocations();

  const getCateringLocationsWithUserAddress = useUpdateCateringLocation();

  const { utm_source: utmSource, utm_campaign } = queryParams;
  const validReferralSource = validateReferralSource(utmSource);
  const params = {};
  if (validReferralSource) {
    params.referralSource = validReferralSource;
  }
  const validUTMCampaign = validateUTMCampaign(utm_campaign);
  if (validUTMCampaign) {
    params.utmCampaign = validUTMCampaign;
  }
  const { isLoading: isCartLoading, data: cart } = useCart({}, params);

  const goToOrderSettingsWithParams = (locationSlug, fulfillmentMethod) => {
    let orderSettingsParams = { locationSlug, fulfillmentMethod };
    if (fulfillmentMethod === fulfillmentOptions.DELIVERY && address) {
      orderSettingsParams = { ...orderSettingsParams, ...address };
    }
    goToOrderSettings(orderSettingsParams);
  };

  const handleBeginOrder = async (locationSlug, fulfillmentMethod) => {
    const cateringLocation = selectCateringLocationBySlug(
      cateringLocations,
      locationSlug
    );

    const hasLocationChanged =
      cart.catering_location && cart.catering_location !== cateringLocation.id;

    const hasCartItems = cart.items.length > 0;

    if (hasLocationChanged && hasCartItems) {
      openLocationChangeNotificationModal({
        onConfirmation: async () => {
          try {
            await updateCart.mutateAsync({
              catering_location_id: cateringLocation.id,
              shipping_street_address_1: null,
              shipping_street_address_2: null,
              shipping_city: null,
              shipping_state: null,
              shipping_postal_code: null,
              shipping_country: '',
              shipping_google_place_id: null,
              fulfillment_date: null,
              fulfillment_time: null
            });
          } finally {
            goToOrderSettingsWithParams(locationSlug, fulfillmentMethod);
          }
        }
      });
    } else {
      goToOrderSettingsWithParams(locationSlug, fulfillmentMethod);
    }
  };

  const handleSortingLocations = async (
    addressComponents,
    selectedLatLng,
    placeId
  ) => {
    const parsedAddress = translateAddressFromComponents(
      addressComponents,
      placeId
    );
    const address = {
      ...parsedAddress,
      shipping_lat: selectedLatLng.lat,
      shipping_lng: selectedLatLng.lng
    };
    setAddress(address);
    getCateringLocationsWithUserAddress.mutate(address);
  };

  if (isLocationsLoading || isCartLoading) {
    return <PageLoadSpinner />;
  }

  // cateringLocationsForPickup, cateringLocationsForDelivery, closedCateringLocations will
  // return empty arrays unless diner has selected an address and locations have been sorted
  const cateringLocationsForPickup = selectPickupLocations(cateringLocations);
  const cateringLocationsForDelivery = selectDeliverToAddressLocations(
    cateringLocations
  );
  const closedCateringLocations = selectClosedLocations(cateringLocations);

  return (
    <>
      <SeoTitleUpdater section="Locations" />
      <CateringLocationScreenHero
        handleSortingLocations={handleSortingLocations}
        locations={cateringLocations}
      />
      <ScreenContainer>
        <AllLocationsList
          closedLocations={closedCateringLocations}
          locationsForPickup={cateringLocationsForPickup}
          locationsThatDeliverToAddress={cateringLocationsForDelivery}
          beginOrder={handleBeginOrder}
          restaurantLocations={cateringLocations}
          LocationsListItemComponent={CateringLocationCard}
        />
      </ScreenContainer>
    </>
  );
}

export default LocationsScreen;
